// PasswordSettings — change your own password, in Kontoinställningar (#343).
//
// The endpoint (PUT /api/users/me/password) and the kit method
// (UserApi.changePassword) have both existed the whole time; nothing in the app
// reached them. Until now the only way to change a password was to log out and
// use "Glömt lösenord", which is a strange thing to ask of someone who is
// already signed in.
//
// Its own <form>, deliberately: AccountSettings already has one for name +
// picture, and nesting forms is invalid HTML — the inner submit would fire the
// outer handler.
import React, { useState } from 'react';
import { KeyRound, Check, Eye, EyeOff } from 'lucide-react';
import { getUserApi } from '@/lib/api/client';
import { IconChip, ActionButton } from '@/components/custom-ui';
import { FormField } from '@/components/dashboard/candidate/profile/FormField';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

const MIN_LENGTH = 8;

// The backend answers EVERY failure with the same code and the same generic
// message — "Failed to change password. Please try again." — and puts the actual
// cause in `details.reason`, in English:
//
//   {"code":"PASSWORD_CHANGE_FAILED","message":"Failed to change password…",
//    "status":500,"details":{"reason":"Incorrect current password"}}
//
// So the reason has to come from details, not from message; reading message
// alone would silently collapse every case into the generic fallback. Matching
// English substrings is fragile, hence the honest default: a reason we did not
// anticipate is shown as "try again" rather than mistranslated.
const swedishError = (err: unknown): string => {
    const e = err as { message?: string; details?: Record<string, unknown> };
    const reason = typeof e?.details?.reason === 'string' ? e.details.reason : (e?.message ?? '');
    const m = reason.toLowerCase();
    if (m.includes('incorrect current password')) {
        return 'Nuvarande lösenord stämmer inte.';
    }
    if (m.includes('does not have a password set')) {
        // #355 made this reachable: employer accounts start with no password, and
        // Google sign-ups never have one. "Glömt lösenord" genuinely works for
        // them — it sets a password rather than replacing one.
        return 'Det här kontot har inget lösenord ännu. Använd "Glömt lösenord?" på inloggningssidan för att sätta ett.';
    }
    if (m.includes('must be different')) {
        return 'Det nya lösenordet måste skilja sig från det nuvarande.';
    }
    if (m.includes('at least 8')) {
        return `Lösenordet måste vara minst ${MIN_LENGTH} tecken.`;
    }
    return 'Kunde inte byta lösenord. Försök igen.';
};

export const PasswordSettings: React.FC = () => {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    const tooShort = next.length > 0 && next.length < MIN_LENGTH;
    const mismatch = confirm.length > 0 && next !== confirm;
    const sameAsCurrent = next.length > 0 && next === current;
    const canSubmit =
        current.length > 0 &&
        next.length >= MIN_LENGTH &&
        next === confirm &&
        !sameAsCurrent &&
        !saving;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        setError(null);
        setDone(false);
        try {
            await getUserApi().changePassword({ old_password: current, new_password: next });
            // Clear immediately on success — leaving a password sitting in three
            // inputs behind a "saved" message is the wrong default on a shared screen.
            setCurrent('');
            setNext('');
            setConfirm('');
            setShow(false);
            setDone(true);
            setTimeout(() => setDone(false), 5000);
        } catch (err: unknown) {
            setError(swedishError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-6`}>
            <div className="flex items-center gap-3 mb-6">
                <IconChip icon={KeyRound} size="lg" />
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">Lösenord</h2>
                    <p className="text-sm text-gray-500">Byt ditt lösenord</p>
                </div>
            </div>

            {done && (
                <Alert className="bg-green-50 border-green-200 mb-4">
                    <Check className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">
                        Lösenordet är ändrat. Du fortsätter vara inloggad här.
                    </AlertDescription>
                </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <FormField
                    label="Nuvarande lösenord"
                    id="current-password"
                    type={show ? 'text' : 'password'}
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    placeholder="••••••••"
                    required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                        label="Nytt lösenord"
                        id="new-password"
                        type={show ? 'text' : 'password'}
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        placeholder="••••••••"
                        helper={`Minst ${MIN_LENGTH} tecken`}
                        required
                    />
                    <FormField
                        label="Bekräfta nytt lösenord"
                        id="confirm-password"
                        type={show ? 'text' : 'password'}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="••••••••"
                        required
                    />
                </div>

                {/* Say what is wrong while they type, rather than after a round trip. */}
                {tooShort && (
                    <p className="text-sm text-red-600">Lösenordet måste vara minst {MIN_LENGTH} tecken.</p>
                )}
                {mismatch && <p className="text-sm text-red-600">Lösenorden matchar inte.</p>}
                {sameAsCurrent && (
                    <p className="text-sm text-red-600">Det nya lösenordet måste skilja sig från det nuvarande.</p>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}

                <div className="flex items-center justify-between gap-4 pt-1">
                    <button
                        type="button"
                        onClick={() => setShow((v) => !v)}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
                    >
                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        {show ? 'Dölj lösenord' : 'Visa lösenord'}
                    </button>
                    <ActionButton type="submit" icon={KeyRound} loading={saving} disabled={!canSubmit}>
                        {saving ? 'Byter…' : 'Byt lösenord'}
                    </ActionButton>
                </div>
            </form>
        </div>
    );
};

export default PasswordSettings;
